/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

//file to collect any custom rxjs operators we write

// declare let Zone: any;
// import "zone.js";

import {
	Observable,
	Subject,
	Subscription,
	MonoTypeOperatorFunction,
	concat,
	of,
	interval,
	combineLatest,
	NEVER,
	BehaviorSubject,
	race,
	merge,
	defer,
	timeout,
	TimeoutError,
	EMPTY,
	pipe,
	connectable,
} from 'rxjs';
import {
	scan,
	tap,
	pluck,
	take,
	debounceTime,
	filter,
	startWith,
	switchMap,
	map,
	materialize,
	dematerialize,
	finalize,
	mapTo,
	catchError,
	retry,
	TapObserver,
	distinct,
	distinctUntilChanged,
} from 'rxjs/operators';

import firebase from 'firebase/compat/app';

import * as _ from 'lodash-es';
import { diff } from 'deep-object-diff';
import { FBDB_ERROR_TIMEOUT } from '@touchplan/strings';
import { logger } from '@touchplan/logging';

// const hiddenZone = Zone.current.fork({name: 'hideFromAngular'});

/** counts the number of events, emits the count on each event */
export function countLive(): MonoTypeOperatorFunction<number> {
	return function countLiveImplementation(source) {
		return source.pipe(scan((count, evt) => count + 1, 0));
	};
}

/**
 * Debounce the first event, then subscribe normally.
 * Covers a few cases like FancyFirebaseList being kind of noisy when it starts up
 * @param  time=5 [description]
 * @return        [description]
 */
export function debounceLoading<T>(time = 5): MonoTypeOperatorFunction<T> {
	return function debounceLoadingImplementation(source) {
		return concat(source.pipe(debounceTime(time), take(1)), source);
	};
}

/** provides a restarting race for multiple ticket lists, (selection/ edit lists)
 * where whichever one actually gets interacted with becomes the only one interacted with
 */
export function ticketListRace<T>(
	listGetter: (thing: T) => Map<string, any>,
	...ticketLists: Array<Observable<T>>
) {
	return combineLatest(ticketLists).pipe(
		//emit when both lists are empty
		filter((lists) => lists.reduce((acc, list) => !acc && !listGetter(list).size, true)),
		switchMap((combinedLists) => {
			const pipedLists = ticketLists.map((list) => {
				return list.pipe(
					filter((res: any) => listGetter(res).size > 0),
					debounceTime(10)
				);
			});
			//always start with the first list, it just needs to be empty. In theory this could be a problem if depending on references of empty maps
			return race(...pipedLists).pipe(startWith(combinedLists[0]));
		})
	);
}

// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7102
//copied and lightly modified from ticketListRace
// TODO - probably want to rewrite, the behavior is a bit silly
export function ticketListRaceModern<T, R>(
	listGetter: (thing: T) => Map<string, unknown>,
	sourceLists: Array<Observable<T>>,
	resultLists: Array<R>
	// ...ticketLists: Array<Observable<T>>
) {
	return combineLatest(sourceLists)
		.pipe(
			//emit when both lists are empty
			filter((lists) => lists.reduce((acc, list) => !acc && !listGetter(list).size, true)),
			switchMap((combinedLists) => {
				const pipedLists = sourceLists.map((list, idx) => {
					return list.pipe(
						filter((res: any) => listGetter(res).size > 0),
						debounceTime(10),
						map(() => resultLists[idx])
					);
				});
				// return race(...pipedLists).pipe(startWith(resultLists[0]));
				return race(...pipedLists);
			})
		)
		.pipe(distinctUntilChanged());
}
// END PLACEHOLDER REPLACE AS PART OF TP-7102

/**
 * drops every n emissions, then emits, then drops, etc.
 * @param  count number of emissions to drop, n > 1
 */
export function drop<T>(count: number, log = false): MonoTypeOperatorFunction<T> {
	const max = count + 1;
	let counter = 0;
	return function dropImplementation(source) {
		return new Observable((subscriber) => {
			return source.subscribe(
				(value) => {
					if (log) {
						logger.log('[drop] counter', counter + '/' + max);
					}
					if (counter % max === 0) {
						subscriber.next(value);
						counter = 0;
					}
					counter += 1;
				},
				(err) => subscriber.error(err),
				() => subscriber.complete()
			);
		});
	};
}

interface IZoneLike {
	run: typeof Zone.current.run;
}

export function runInZone<T>(zone: IZoneLike): MonoTypeOperatorFunction<T> {
	return function mySimpleOperatorImplementation(source) {
		return new Observable<T>((observer) => {
			const next = (value: T) =>
				zone.run(() => {
					observer.next(value);
				});
			const error = (e: any) => zone.run(() => observer.error(e));
			const complete = () => zone.run(() => observer.complete());
			return source.subscribe({ next, error, complete });
		});
	};
}

export function alternativeBuffer<T>(bufferObs: Observable<any>) {
	return function alternativeBufferImplementation(srcObs: Observable<T>) {
		return new Observable<T[]>((subscriber) => {
			let queue: T[] = [];
			let hasStuff = false;

			const unsubs: Subscription[] = [];

			unsubs.push(
				srcObs.subscribe({
					next: (value) => {
						queue.push(value);
						hasStuff = true;
					},
					error: (err) => subscriber.error(err),
					complete: () => subscriber.complete(),
				})
			);

			unsubs.push(
				bufferObs.subscribe(
					(t) => {
						if (hasStuff) {
							// logger.time("[alternativeBuffer] frame ended");
							subscriber.next(queue);
							queue = [];
							hasStuff = false;
						}
					},
					(err) => subscriber.error(err),
					() => subscriber.complete()
				)
			);

			return () => {
				unsubs.forEach((unsub) => unsub.unsubscribe());
			};
		});
	};
}

/*
 * functionally this is the same as native Rxjs BufferTime
 * the key difference is BufferTime always emits an array after buffer time is up, empty array if there are no emissions
 * while alternativeBufferTime doesn't emit anything if there are no emissions during the buffer.
 */
export function alternativeBufferTime<T>(time: number) {
	return alternativeBuffer<T>(interval(time));
}

export function pluckList<T>(source: Observable<T>, keyList: string[]) {
	const output: { [key: string]: Observable<any> } = {};
	keyList.forEach((key) => (output[key] = source.pipe(pluck(key))));
	return output;
}

export class DelayedRetryError<T = any> extends Error {
	constructor(public override message: string, public source: Observable<T>) {
		super(message);
	}
}

/**
 * Waits for a set of data to fully load determined by the provided condition.
 * Currently mainly useful for data that loads "in place" like a fancyFirebaseList.
 * Incidentally that loading in place part is why this needs to be so custom
 * While the conditional is met, this will mirror the source observable (with some extra overhead)
 * @param  successFn return true when the data can be considered "loaded"
 * @param  retryDelay delay time for retries
 * @param  retryCount number of times to retry
 * @return        [description]
 *
 */
export function delayedRetry<T>(
	successFn: (output: T) => boolean,
	retryDelay = 500,
	retryCount = 5
): MonoTypeOperatorFunction<T> {
	return function delayedRetryImplementation(srcObs) {
		return srcObs.pipe(
			switchMap((value) => {
				return interval(retryDelay).pipe(
					map((count) => {
						if (count >= retryCount) {
							throw new DelayedRetryError('delayedRetry - maximum retries exceeded', srcObs);
						}
						return value;
					}),
					startWith(value),
					materialize(),
					switchMap((notif) => {
						//success passed
						if (notif.kind === 'N' && successFn(notif.value)) {
							// return of(notif, new Notification<T>('C'));
							return of(notif, { kind: 'C' });
						} else if (notif.kind === 'N') {
							return EMPTY;
						} else {
							return of(notif);
						}
					}),
					dematerialize()
				);
			})
		);
	};
}

/**
 * variant of combineLatest that does not wait until everything emits once. This
 * version starts emitting as soon as any observable emits
 * @return                          [description]
 */
export function noWaitCombineLatest<T>(...observables: Array<Observable<T>>): Observable<T[]> {
	const arr = observables.map((o) => o.pipe(startWith(null as T)));
	return combineLatest(arr).pipe(filter((res) => res.every((val) => val === null)));
}

export class ObservableSink {
	private sink: Subscription[] = [];

	public add(subscription: Subscription) {
		this.sink.push(subscription);
	}

	public unsubscribe() {
		this.sink.forEach((sink) => sink.unsubscribe());
	}
}

//TODO - enhance with "maintain the zone" shit
/** takes the same set of arguements a firebase on call would take, but returns an observable */
export function fromFirebaseRef(type: firebase.database.EventType, ref: firebase.database.Reference) {
	return new Observable<firebase.database.DataSnapshot>(function subscribe(subscriber) {
		const offFn = ref.on(
			type,
			(snapshot) => {
				subscriber.next(snapshot);
			},
			(err) => {
				subscriber.error(err);
				ref.off(type, offFn);
			}
		);

		return function unsubscribe() {
			ref.off(type, offFn);
		};
	});
}

/**
 * Pauseable Observable
 * Easily lets us create pausable observables
 */
export class PausableObservable<T> extends Observable<T> {
	private pauser: BehaviorSubject<boolean>;

	public pause() {
		this.pauser.next(true);
	}

	public resume() {
		this.pauser.next(false);
	}
}
// Pauseable operator
export function pausable() {
	return function pauseFn<T>(source: Observable<T>) {
		const pausableProto = PausableObservable.prototype;

		const pauser = new BehaviorSubject(false);
		const newSource = pauser.pipe(
			switchMap((paused) => (paused ? NEVER : source.pipe(materialize()))),
			dematerialize()
		);

		const pausable: any = Object.create(newSource, {
			pause: { value: pausableProto.pause },
			resume: { value: pausableProto.resume },
			pauser: { value: pauser },
		});
		return pausable as PausableObservable<T>;
	};
}

/**
 * A variant of combineLatest/ zip that groups together multiple observables into a single stream containing a emission from each.
 * This one however only emits when each included stream emits once in the order they are passed into the operator.
 * This is very similar to zip with two exceptions, zip does not enforce an ordering, and zip preserves all emissions instead of
 * just emitting either the most recent or the oldest.
 * Because of javascript "frames", this operator will not work as you might expect with "syncronous" observables.
 * Imagine passing in two of these `of('a','b','c')`
 * Since each one of executes their entire stream immediately, you'll get a single emission of ['c', 'a'] instead of the potentially expected
 * 3 emissions.
 * @param inputObservables Any array of observables to combine
 * @param useFirst Special flag that changes duplicate emission behavior to use the first one instead of use the most recent
 */
export function combineSequence<T>(inputObservables: Array<Observable<T>>, useFirst = false) {
	const decoratedObservables = inputObservables.map((o, idx) => {
		return o.pipe(map((emission) => ({ idx, val: emission })));
	});

	return merge(...decoratedObservables).pipe(
		scan((acc, val) => {
			//setup the index number to wait for, loop around if necessary
			let targetIdx;
			let newArray: Array<T>;

			//reset
			if (acc.length === inputObservables.length) {
				targetIdx = 0;
				newArray = [];
			}
			//continue
			else {
				targetIdx = acc.length;
				newArray = [...acc];
			}

			if (val.idx === targetIdx) {
				newArray[val.idx] = val.val;
			} else if (val.idx < targetIdx && !useFirst) {
				newArray[val.idx] = val.val;
			}

			return newArray;
		}, [] as Array<T>),
		filter((arr) => arr.length === inputObservables.length)
	);

	// const arr = observables.map(o => o.pipe(startWith(null as T)));
	// return combineLatest(arr).pipe(filter(res => res.every(val => val === null)));
}

/**
 * Turn a key value into array
 */
export function mapKeysToArray<T>() {
	return function mapKeysToArrayImplementation(source: Observable<Map<string, T>>) {
		return source.pipe(map((val) => [...val.keys()]));
	};
}

/**
 * Turn a map value into array
 */
export function mapValuesToArray<T>() {
	return function mapValuesToArrayImplementation(source: Observable<Map<string, T>>) {
		return source.pipe(map((val) => [...val.values()]));
	};
}

/**
 * Sort array based on given compare function, array from the stream, returns a new array
 */
export function simpleSortArray<T>(
	compareFunction: (a: any, b: any) => number
): MonoTypeOperatorFunction<T[]> {
	return function sortArrayImplementation(source: Observable<T[]>) {
		return new Observable((observer) => {
			return source.subscribe({
				next(val) {
					observer.next(
						Array.isArray(val) && val.length > 0 && compareFunction ? [...val.sort(compareFunction)] : val
					);
				},
				error(error) {
					observer.error(error);
				},
				complete() {
					observer.complete();
				},
			});
		});
	};
}

/**
 * Sort array based on given compare function observable, array from the stream, returns a new array
 * Similar to simpleSortArray
 */
export function sortArray(compareFunction: Observable<(a: any, b: any) => number>) {
	return function sortArrayImplementation(source: Observable<any>) {
		return combineLatest([source, compareFunction]).pipe(
			map((combined) => {
				const arr = combined[0];
				const compareFunc = combined[1];
				return Array.isArray(arr) && arr.length > 0 && compareFunction ? [...arr.sort(compareFunc)] : arr;
			})
		);
	};
}

/**
 * Takes a stream, creates deep clones of every emission and records them. Uses those clones to calculate a diff between emissions.
 * Emits a summary array on every emission of the source array with that information. Can be used to compare what specifically happened
 * on each emission. Primarily useful for tracking larger data. Function is exposed on window, and is intended to be run from the console.
 * Sample: `planState.tickets.rawEvent$.pipe(debugStream()).subscribe(a => console.log('==========', a))`
 * @param source Target observable to be recorded
 */
export function debugStream<T>(): MonoTypeOperatorFunction<T[]> {
	return function debugStreamImplementation(source) {
		return source.pipe(
			scan((acc, val) => {
				let d;
				if (acc.length > 0 && typeof val === 'object') {
					d = diff(acc[acc.length - 1].snapshot, val as any);
				}
				const anyVal = val as any;
				acc.push({
					type: anyVal && anyVal.type ? anyVal.type : undefined,
					snapshot: _.cloneDeep(val),
					diff: d,
				});
				return acc;
			}, [])
		);
	};
}
(window as any).debugStream = debugStream;

interface SubjectLike<T> {
	next: (val: T) => void;
	error: (err: any) => void;
}
/**
 * Takes a subject-like and returns an subscription compatible object so that a subject can subscribe to another observable without
 * subscribing to the completion. This is useful when you want to connect multiple shortlived instances of the same observable
 * to a long lived subject.
 * Should serve as a 1 to 1 conversion from:
 * `ob$.subscribe(aSubject)`
 * to
 * `ob$.subscribe(subjectSubscribeWithoutComplete(aSubject))`
 */
export function subjectSubscribeWithoutComplete<T>(subject: SubjectLike<T>) {
	return {
		next: (val: T) => subject.next(val),
		error: (err: any) => subject.error(err),
	};
}

/**
 * Special version of finalize that returns the last value emitted
 */
export function finalizeWithValue<T>(callback: (value: T) => void) {
	return (source: Observable<T>) =>
		defer(() => {
			let lastValue: T;
			return source.pipe(
				tap((value) => (lastValue = value)),
				finalize(() => callback(lastValue))
			);
		});
}

/**
 * A custom operator that emits whenever the source emits, or emits when a notifier fires
 * @param notification$ An observable, the value is ignore, the emission is the only interesting part
 */
export function sampleWithSource<T>(notification$: Observable<any>) {
	return (sourceOb$: Observable<T>) => {
		return sourceOb$.pipe(
			switchMap((stuffBeingEdited) => {
				return notification$.pipe(startWith(stuffBeingEdited), mapTo(stuffBeingEdited));
			})
		);
	};
}

export class TimeoutLocalizedError<T = any> extends TimeoutError {
	constructor() {
		super();
		this.message = FBDB_ERROR_TIMEOUT;
	}
}

export function timeoutLocalized(due: number) {
	return function timeoutImpl(source: Observable<any>) {
		return source.pipe(
			timeout(due),
			catchError((err) => {
				if (err instanceof TimeoutError) {
					throw new TimeoutLocalizedError();
				}
				throw err;
			})
		);
	};
}

/**
 * Adds a timeout to the observable and retry the observable by given intent times.
 * @param timeoutInterval time in miliseconds for timeout
 * @param intents number of intents it the observable will be called on error
 * @returns the piped observable
 */
export function withTimeoutRetryStrategy<T>(
	timeoutInterval: number,
	intents: number
): MonoTypeOperatorFunction<T> {
	return pipe(timeout(timeoutInterval), retry(intents));
}

/**
 * Little utility thing to quickly subscrible to every event in an observable
 * ob$.pipe(tap(logAllTheTap('blah$'))).subscribe()
 * //blah$ - tap subscribe
 */
export function logAllTheTap<T>(identiferText: string): Partial<TapObserver<T>> {
	return {
		next: (value) => console.log(identiferText, '- tap next', value),
		error: (error) => console.log(identiferText, '- tap error', error),
		complete: () => console.log(identiferText, '- tap complete'),
		subscribe: () => console.log(identiferText, '- tap subscribe'),
		unsubscribe: () => console.log(identiferText, '- tap unsubscribe'),
		finalize: () => console.log(identiferText, '- tap finalize'),
	};
}

/**
 * Allow for lazy loading a long lived observable, based on some signal
 * @param cb function that returns the observable, think... angularfire's .object().valueChanges()
 * @param startSignal$ as soon as this observable emits, the cb will be called
 * @param finishSignal$ "destroy$"
 * TODO - this should be tested, but this pr is getting too long lived
 */
export function lazyLoadWhen<T>(
	cb: () => Observable<T>,
	startSignal$: Observable<unknown>,
	finishSignal$: Observable<unknown>
) {
	//requirements
	// - the observable to be lazy loaded _must_ be provided as a function in some sense, no other
	// way to generically "execute code later"
	// - the code that runs "connect", must also be able to know when "un-connect" (unsub)
	// - and some instruction for when it's time to lazy is needed (observable, function, etc)
	//
	// At minimum that means a function returning an observable, a trigger observable and relying
	// on the returned observable's completion to clean up the internal subject
	// but that's not super flexible, nor very "standard"
	const lazyLoadedOb$ = concat(
		startSignal$.pipe(
			take(1),
			switchMap(() => EMPTY)
		),
		defer(cb)
	);

	const multiplexedOb$ = connectable<T>(lazyLoadedOb$, {
		//very poorly documented, but it seems like unsubing from the connect function is
		//way to clean up the internal subject
		connector: () => new BehaviorSubject(null),
		resetOnDisconnect: false,
	});
	const unsub = multiplexedOb$.connect();
	finishSignal$.pipe(take(1)).subscribe(() => unsub.unsubscribe());

	return multiplexedOb$;
}

/**
 * Converts a given File object to a Base64-encoded string using FileReader.
 * Returns an Observable that emits the Base64 string once the file is fully read.
 * If the Observable is unsubscribed before completion, the file reading process is aborted.
 *
 * Note: Currently, not all FileReader events are being handled — only the 'load' and 'error' events
 * are used to emit the result or an error. Other events like 'progress' or 'abort' are not mapped at this time.
 *
 * @param file - The File object to convert.
 * @returns Observable<string> - Emits the Base64 string representation of the file.
 */
export function convertFileToBase64$(file: File): Observable<string> {
	return new Observable((observer) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => {
			observer.next(reader.result as string);
			observer.complete();
		};
		reader.onerror = (error) => {
			observer.error(error);
		};

		// Cleanup logic on unsubscribe
		return () => {
			if (reader.readyState === FileReader.LOADING) {
				reader.abort();
			}
		};
	});
}
